import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { bondsAPI, formatDate, formatCurrency } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const BondDetails = () => {
  const { bondId } = useParams();
  const navigate = useNavigate();

  // Fetch bond data
  const {
    data: bondData,
    isPending: isLoading,
    error,
  } = useQuery({
    queryKey: ["bond", bondId],
    queryFn: () => bondsAPI.getBond(bondId),
    select: (response) => {
      const responseData = response?.data;
      if (responseData?.success && responseData?.data) {
        return responseData.data.bond;
      }
      return responseData?.bond || responseData?.data || null;
    },
    enabled: !!bondId,
  });


  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading bond details..." />;
  }

  if (error || !bondData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading bond details</p>
          <button
            onClick={() => navigate("/trustee/bonds")}
            className="text-blue-600 hover:text-blue-900"
          >
            Back to Bonds
          </button>
        </div>
      </div>
    );
  }

  const bond = bondData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/trustee/bonds")}
                className="mr-4 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {bond.bondName}
                </h1>
                <p className="text-sm text-gray-500">{bond.bondNumber}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Bond Information
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Bond Name
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bond.bondName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Bond Number
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bond.bondNumber}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Bond Type
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bond.productType === "Fixed Notes" ? "Conventional" : (bond.productType || "N/A")}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Status
                  </dt>
                  <dd className="mt-1">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        bond.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {bond.status}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Financial Details
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Issue Amount
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatCurrency(bond.issueAmount, bond.currency)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Coupon Rate
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bond.couponRate}%
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Minimum Investment
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatCurrency(
                      bond.minimumInvestmentValue,
                      bond.currency
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Currency
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bond.currency || "MYR"}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Dates
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Issue Date
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bond.issueDate ? formatDate(bond.issueDate) : "N/A"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Maturity Date
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bond.maturityDate
                      ? formatDate(bond.maturityDate)
                      : "N/A"}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BondDetails;
